import { useEffect, useState } from 'react';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { UiContainer } from '../../state/ui';
import { ApiList } from '../../types';
import { useDefaultVatMultiplier } from '../../utils/vatUtils';
import { renderTemplate } from '../utils';
import './RefillCost.css';

/**
 * What this refill would cost if it went today, and what it would earn back. None of it can be
 * worked out here - freight turns on the free-shipping limit, and the bonus on the customer's
 * level, the configured step and which products earn bonus at all - so kg-api adds it up.
 *
 * It is an estimate and says so: the order is created later, with the prices of that day.
 */
export const RefillCost = ({ list }: { list: ApiList }): JSX.Element => {
	const translations = useTranslation();
	const t = { ...translations.subscriptions, cart: translations.cart };
	const { asPrice } = useFormatting();
	const ui = UiContainer.useContainer();
	const vatMultiplier = useDefaultVatMultiplier(ui.priceWithVat);

	// nothing to add up, and an api that has not been released with the endpoint answers nothing
	const { data: totals, mutate } = useApi(list.items.length ? Res.listTotals : null, { listId: list.id });

	/*
	 * Changing a quantity revalidates the lists, not this - the url is the same either way, so swr
	 * would go on serving the figures the old basket added up to. The new products would be listed
	 * above a subtotal, a freight and a bonus belonging to the ones before them.
	 *
	 * `addedUpFor` is the basket the figures on screen were fetched for, and it only moves once the
	 * new ones have arrived. So nothing is shown between asking and being answered, and nothing is
	 * shown at all if the asking fails - no number here is ever about a basket other than this one.
	 */
	const basket = list.items.map((item) => `${item.product.id}:${item.quantity}`).join(',');
	const [addedUpFor, setAddedUpFor] = useState(basket);

	useEffect(() => {
		if (addedUpFor === basket) return;

		let stillWanted = true;

		mutate().then(
			() => stillWanted && setAddedUpFor(basket),
			// retries are off for this endpoint, so a failure leaves the block out rather than lying
			() => undefined
		);

		return () => {
			stillWanted = false;
		};
	}, [basket, addedUpFor]);

	if (!totals || addedUpFor !== basket) return <></>;

	const { subTotal, subTotalVat, shippingFee, vat, total, estimatedBonus, bonusLevel } = totals;

	return (
		<div className="refill-cost">
			<div className="refill-cost-row">
				<span>{t.cart.subTotal}</span>
				<span>{asPrice(subTotal + (ui.priceWithVat ? subTotalVat : 0))}</span>
			</div>
			<div className="refill-cost-row">
				<span>{t.cart.delivery}</span>
				<span>{asPrice(shippingFee * vatMultiplier)}</span>
			</div>
			<div className="refill-cost-row total">
				<strong>{ui.priceWithVat ? t.cart.totalIncVatHeading : t.totalExVat}</strong>
				<strong>{asPrice(ui.priceWithVat ? total : total - vat)}</strong>
			</div>
			{/*
			 * Null is a customer who earns no bonus at all, which is not the same as earning nothing
			 * on this one - that is 0, and worth showing, since adding more would change it.
			 */}
			{estimatedBonus !== null && bonusLevel !== null && (
				<div className="refill-cost-row bonus">
					<span>{renderTemplate(t.estimatedBonus, { percent: Math.round(bonusLevel * 100) })}</span>
					<span>{asPrice(estimatedBonus)}</span>
				</div>
			)}
			<p className="refill-cost-notice">{t.priceMayChange}</p>
		</div>
	);
};
